import { AfterViewInit, Component, effect, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatRippleModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoModule, TranslocoPipe } from '@jsverse/transloco';
import { ApexOptions, NgApexchartsModule } from 'ng-apexcharts';
import { AuthService } from 'app/core/auth/auth.service';
import { Router } from '@angular/router';
import { TotalRevenueComponent } from "../total-revenue/total-revenue.component";
import { RangeKey, VehicleStockComponent } from '../vehicle-stock/vehicle-stock.component';
import { MandatAdminDashboardService, MandatAdminDashboardSummaryResponse, MandatAdminDashboardTopBuyersDto, MandatAdminDashboardTopSellersDto } from './mandat-dashboard.service';
import { AlertService } from 'app/core/services/alert.service';

@Component({
  selector: 'app-mandat-dashboard',
  templateUrl: './mandat-dashboard.component.html',
  styleUrls: ['./mandat-dashboard.component.css'],
  imports: [
    TranslocoModule,
    MatIconModule,
    MatButtonModule,
    MatRippleModule,
    MatMenuModule,
    MatTabsModule,
    NgApexchartsModule,
    MatTableModule,
    MatDividerModule,
    MatSortModule,
    MatProgressBarModule,
    CommonModule,
    CurrencyPipe,
    TotalRevenueComponent,
    MatButtonToggleModule,
    VehicleStockComponent,
    TranslocoPipe,
  ]
})
export class MandatDashboardComponent implements OnInit, AfterViewInit {
  selectedTabIndex: number = 0;

  data = signal<MandatAdminDashboardSummaryResponse>({
    yearlySummary: undefined,
    vehicleStock: undefined,
    topBuyers: undefined,
    topSellers: undefined,
    delivered: 0,
    upcomingDelivery: 0,
    finishedTestDrives: 0,
    upcomingTestDrives: 0
  });


  now = new Date();
  setYear = signal<number>(this.now.getFullYear());
  range = signal<RangeKey>('30 days');

  chartConversions: ApexOptions;

  @ViewChild('recentTransactionsTable', { read: MatSort })
  recentTransactionsTableMatSort: MatSort;
  chartTopSellers: ApexOptions;
  chartTopBuyers: ApexOptions;
  soldCarsTimePeriod = signal<string>('Last 7 Days');
  sellersPeriod = signal<string>('30 days');
  buyersPeriod = signal<string>('30 days');

  topSellersDataSource: MatTableDataSource<MandatAdminDashboardTopSellersDto> =
    new MatTableDataSource();

  topBuyersDataSource: MatTableDataSource<MandatAdminDashboardTopBuyersDto> =
    new MatTableDataSource();

  defaultColors = [
    '#319795', '#4FD1C5', '#ED8936', '#ECC94B', '#805AD5', '#CBD5E0', '#F56565', '#68D391', '#63B3ED'
  ];

  recentTransactionsTableColumns: string[] = [
    'sellerName',
    'amount',
    'total',
  ];

  constructor(private _router: Router, public authService: AuthService, private _mandatDashboardService: MandatAdminDashboardService, private alert: AlertService) {
    effect(() => {
      this.getData();
    });
  }

  ngOnInit() {
    window['Apex'] = {
      chart: {
        events: {
          mounted: (chart: any, options?: any): void => {
            this._fixSvgFill(chart.el);
          },
          updated: (chart: any, options?: any): void => {
            this._fixSvgFill(chart.el);
          },
        },
      },
    };
  }
  getData() {
    this._mandatDashboardService.getMandatAdminDashboardData(this.range(), this.setYear(), this.sellersPeriod(), this.buyersPeriod()).subscribe({
      next: (response: any) => {
        this.data.set(response.value);
        this.topSellersDataSource.data = this.data().topSellers;
        this.topBuyersDataSource.data = this.data().topBuyers;
        setTimeout(() => this._prepareChartData(), 300);
      },
      error: (error) => {
        this.alert.errorFromHttp(error);
      },
    })
  }

  ngAfterViewInit(): void {
    // Make the data source sortable
    this.topSellersDataSource.sort =
      this.recentTransactionsTableMatSort;
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  private _fixSvgFill(element: Element): void {
    const currentURL = this._router.url;
    Array.from(element.querySelectorAll('*[fill]'))
      .filter((el) => el.getAttribute('fill').indexOf('url(') !== -1)
      .forEach((el) => {
        const attrVal = el.getAttribute('fill');
        el.setAttribute(
          'fill',
          `url(${currentURL}${attrVal.slice(attrVal.indexOf('#'))}`
        );
      });
  }

  private _prepareChartData(): void {
    this.prepareTopSellersData();
    this.prepareTopBuyersData();
  }
  prepareTopBuyersData() {
    var labelsTopBuyers = [];
    var seriesTopBuyers = [];
    for (const buyer of this.topBuyersDataSource.data) {
      labelsTopBuyers.push(buyer.buyerName);
      seriesTopBuyers.push(buyer.percentageOfTotalBought);
    }

    // Sum and add "Others" if < 100
    const total = seriesTopBuyers.reduce((a, b) => a + b, 0);
    const remainder = Math.max(0, +(100 - total).toFixed(2)); // keep 2 decimals & avoid negatives

    if (remainder > 0.01) { // ignore tiny float leftovers
      labelsTopBuyers.push('Others');
      seriesTopBuyers.push(remainder);
    }

    // Visits
    this.chartTopBuyers = {
      chart: {
        animations: {
          speed: 400,
          animateGradually: {
            enabled: false,
          },
        },
        fontFamily: 'inherit',
        foreColor: 'inherit',
        height: '100%',
        type: 'donut',
        sparkline: {
          enabled: true,
        },
      },
      colors: this.defaultColors.slice(0, seriesTopBuyers.length),
      labels: labelsTopBuyers,
      plotOptions: {
        pie: {
          customScale: 0.9,
          expandOnClick: false,
          donut: {
            size: '70%',
          },
        },
      },
      series: seriesTopBuyers,
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
        active: {
          filter: {
            type: 'none',
          },
        },
      },
      tooltip: {
        enabled: true,
        fillSeriesColor: false,
        theme: 'dark',
        custom: ({
          seriesIndex,
          w,
        }): string => `<div class="flex items-center h-8 min-h-8 max-h-8 px-3">
                                                     <div class="w-3 h-3 rounded-full" style="background-color: ${w.config.colors[seriesIndex]};"></div>
                                                     <div class="ml-2 text-md leading-none">${w.config.labels[seriesIndex]}:</div>
                                                     <div class="ml-2 text-md font-bold leading-none">${w.config.series[seriesIndex]}%</div>
                                                 </div>`,
      },
    };
  }
  prepareTopSellersData() {
    var labelsTopSellers = [];
    var seriesTopSellers = [];
    for (const seller of this.topSellersDataSource.data) {
      labelsTopSellers.push(seller.sellerName);
      seriesTopSellers.push(seller.percentageOfTotalRevenue);
    }

    // Sum and add "Others" if < 100
    const total = seriesTopSellers.reduce((a, b) => a + b, 0);
    const remainder = Math.max(0, +(100 - total).toFixed(2)); // keep 2 decimals & avoid negatives

    if (remainder > 0.01) { // ignore tiny float leftovers
      labelsTopSellers.push('Others');
      seriesTopSellers.push(remainder);
    }

    // Visits
    this.chartTopSellers = {
      chart: {
        animations: {
          speed: 400,
          animateGradually: {
            enabled: false,
          },
        },
        fontFamily: 'inherit',
        foreColor: 'inherit',
        height: '100%',
        type: 'donut',
        sparkline: {
          enabled: true,
        },
      },
      colors: this.defaultColors.slice(0, seriesTopSellers.length),
      labels: labelsTopSellers,
      plotOptions: {
        pie: {
          customScale: 0.9,
          expandOnClick: false,
          donut: {
            size: '70%',
          },
        },
      },
      series: seriesTopSellers,
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
        active: {
          filter: {
            type: 'none',
          },
        },
      },
      tooltip: {
        enabled: true,
        fillSeriesColor: false,
        theme: 'dark',
        custom: ({
          seriesIndex,
          w,
        }): string => `<div class="flex items-center h-8 min-h-8 max-h-8 px-3">
                                                     <div class="w-3 h-3 rounded-full" style="background-color: ${w.config.colors[seriesIndex]};"></div>
                                                     <div class="ml-2 text-md leading-none">${w.config.labels[seriesIndex]}:</div>
                                                     <div class="ml-2 text-md font-bold leading-none">${w.config.series[seriesIndex]}%</div>
                                                 </div>`,
      },
    };
  }
}